// pages/integrations/Tickets.tsx
import React from "react";
import { Ticket, Globe, Database, Key, Check, Copy } from "lucide-react";
import useAuth from "../../../../hooks/features/auth/useAuth";
import { Entreprise } from "../../../../types/features/Entreprise";
import {
  IntegrationHeader,
  ApiKeyTable,
  ApiKeyModal,
  ApiDeleteConfirmationModal,
  NewKeyNotification,
  CodeExamples,
  ApiTester,
  FeatureList,
  TicketConfig,
} from "../../../../components/features/integrations";
import { useApiKeyModule } from "../../../../hooks/features/apiKeys/useApiKeyModule";
import { MAIN_URL } from "../../../../config/api";
import useModules from "../../../../hooks/features/modules/useModules";
import type { EndpointExample } from "../../../../components/features/integrations/CodeExamples";
import type { TesterEndpoint } from "../../../../components/features/integrations/ApiTester";

const MODULE = "tickets";
const MODULE_LABELS = {
  tickets: "Tickets",
  calls: "Appels",
  email: "Email",
  whatsapp: "WhatsApp",
  sms: "SMS",
  webhook: "Webhook",
  all: "Accès complet",
};

const PERMISSIONS = ["create", "read", "update", "delete", "send", "receive"];

export const TicketsIntegrationPage: React.FC = () => {
  const { user } = useAuth();
  const entreprise = user?.profile as Entreprise;
  const originUrl = window.location.origin;
  const { entrepriseModules } = useModules();
  const ticketModule = entrepriseModules?.find((m) => m?.name === "tickets" || m?.key === "tickets");
  const isTicketActive = ticketModule?.is_active || false;

  const {
    apiKeys,
    moduleKey,
    loading,
    copied,
    isModalOpen,
    keyToDelete,
    isRegenerating,
    newKeySecret,
    formData,
    setFormData,
    setIsModalOpen,
    setKeyToDelete,
    setNewKeySecret,
    copyToClipboard,
    handleCreateKey,
    handleRegenerate,
    handleDeleteKey,
    handleToggleKey,
  } = useApiKeyModule(MODULE);

  const slug =
    entreprise?.name
      ?.toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "";

  const config = {
    companyId: entreprise?.id || "",
    apiKey: moduleKey?.key || "Aucune clé API trouvée",
    publicLink: `${originUrl}/ticket/${slug}/${entreprise?.id || ""}`,
    baseUrl: `${MAIN_URL}/api/v1/tickets`,
  };

  // ✅ Endpoints pour CodeExamples (URL complète)
  const codeEndpoints: EndpointExample[] = [
    {
      key: "tickets-store",
      label: "Créer un ticket",
      method: "POST",
      endpoint: `${config.baseUrl}`,
      body: {
        entreprise_id: config.companyId,
        contact: {
          email: "client@example.com",
          name: "Jean Dupont",
          phone: "+33612345678",
        },
        subject: "Problème de connexion",
        description: "Je n'arrive plus à me connecter à mon compte",
        priority: "haute",
        category: "connexion",
      },
    },
    {
      key: "tickets-show",
      label: "Voir un ticket",
      method: "GET",
      endpoint: `${config.baseUrl}/{id}`,
      body: {},
    },
    {
      key: "tickets-reference",
      label: "Par référence",
      method: "GET",
      endpoint: `${config.baseUrl}/reference/{reference}`,
      body: {},
    },
  ];

  // ✅ Endpoints pour ApiTester (chemin relatif)
  const testerEndpoints: TesterEndpoint[] = [
    {
      key: "tickets-store",
      label: "Créer un ticket",
      method: "POST",
      endpoint: "/v1/tickets",
      body: {
        contact_email: "test@test.com",
        contact_name: "Jean Test",
        contact_phone: "+33612345678",
        subject: "Test depuis le dashboard",
        description: "Description du test",
        priority: "moyenne",
        category: "",
      },
    },
    {
      key: "tickets-reference",
      label: "Par référence",
      method: "GET",
      endpoint: "/v1/tickets/reference/{reference}",
      body: {},
    },
  ];

  const features = ["Tickets automatiques", "Suivi en temps réel", "Pièces jointes", "Notifications email"];

  if (!entreprise) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <IntegrationHeader
        icon={<Ticket size={40} className="text-blue-600 flex-shrink-0" />}
        title="Tickets client"
        description="Gérez les demandes de vos clients via un formulaire public ou votre propre site"
        status="Actif"
        statusColor="green"
        gradientFrom="blue-50"
        gradientTo="indigo-50"
      />

      {/* Option 1 - Formulaire hébergé */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200 p-5 bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="flex items-center gap-3">
            <Globe size={20} className="text-green-600" />
            <h3 className="font-semibold text-slate-800">Option 1 – Formulaire hébergé</h3>
            <span className="ml-auto text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-medium">
              Simple & rapide
            </span>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-600">
            Partagez ce lien avec vos clients. Ils remplissent le formulaire et les tickets arrivent directement dans
            votre espace.
          </p>

          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Votre lien public</label>
            <div className="flex items-center gap-2 mt-1.5">
              <code className="flex-1 bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm font-mono text-slate-700 truncate">
                {config.publicLink}
              </code>
              <button
                onClick={() => copyToClipboard(config.publicLink, "publicLink")}
                className="p-2.5 bg-white hover:bg-slate-100 rounded-lg transition-colors border border-slate-200"
              >
                {copied === "publicLink" ? (
                  <Check size={18} className="text-green-600" />
                ) : (
                  <Copy size={18} className="text-slate-600" />
                )}
              </button>
              <button
                onClick={() => window.open(config.publicLink, "_blank")}
                className="p-2.5 bg-green-600 hover:bg-green-700 rounded-lg transition-colors text-white"
              >
                <Globe size={18} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-lg">
              <Check size={14} className="text-green-600" />
              Aucun code requis
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-lg">
              <Check size={14} className="text-green-600" />
              Mise en ligne instantanée
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-lg">
              <Check size={14} className="text-green-600" />
              Design responsive
            </div>
          </div>
        </div>
      </div>

      {/* Option 2 - Intégration API */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200 p-5 bg-gradient-to-r from-purple-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <Database size={20} className="text-purple-600" />
            <h3 className="font-semibold text-slate-800">Option 2 – Intégration API</h3>
            <span className="ml-auto text-xs bg-purple-100 text-purple-700 px-2.5 py-1 rounded-full font-medium">
              Flexibilité totale
            </span>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-600">
            Créez votre propre formulaire sur votre site et envoyez les données via notre API.
          </p>

          {/* ID Entreprise */}
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">ID Entreprise</label>
            <div className="flex items-center gap-2 mt-1">
              <code className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm font-mono text-slate-600">
                {config.companyId}
              </code>
              <button
                onClick={() => copyToClipboard(config.companyId, "companyId")}
                className="p-2.5 bg-white hover:bg-slate-100 rounded-lg transition-colors border border-slate-200"
              >
                {copied === "companyId" ? (
                  <Check size={18} className="text-green-600" />
                ) : (
                  <Copy size={18} className="text-slate-600" />
                )}
              </button>
            </div>
          </div>

          {/* Clé API */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Clé API {MODULE}</label>
              <button
                onClick={() => setIsModalOpen(true)}
                className="p-1 text-primary hover:bg-primary/10 rounded transition-colors"
                title="Créer une clé"
              >
                <Key size={16} />
              </button>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <code className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm font-mono text-slate-600 break-all">
                {config.apiKey}
              </code>
              <button
                onClick={() => copyToClipboard(config.apiKey, "apiKey")}
                className="p-2.5 bg-white hover:bg-slate-100 rounded-lg transition-colors border border-slate-200"
              >
                {copied === "apiKey" ? (
                  <Check size={18} className="text-green-600" />
                ) : (
                  <Copy size={18} className="text-slate-600" />
                )}
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              {loading ? "Chargement..." : `Cette clé est utilisée pour les appels API du module ${MODULE}`}
            </p>
          </div>
        </div>
      </div>

      {/* ✅ Exemples de code multi-endpoints */}
      <CodeExamples apiKey={config.apiKey} endpoints={codeEndpoints} />

      {/* ✅ Testeur API multi-endpoints */}
      <ApiTester endpoints={testerEndpoints} apiKey={config.apiKey} companyId={config.companyId} moduleName={MODULE} />

      {/* Gestion des clés API */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200 p-5 bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Key size={20} className="text-amber-600" />
              <h3 className="font-semibold text-slate-800">Gestion des clés API</h3>
              <span className="text-xs bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full font-medium">
                {apiKeys.length} clé(s)
              </span>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm"
            >
              <Key size={16} />
              Nouvelle clé
            </button>
          </div>
        </div>

        <div className="p-5">
          <ApiKeyTable
            apiKeys={apiKeys}
            loading={loading}
            moduleLabels={MODULE_LABELS}
            onCopy={copyToClipboard}
            onToggle={handleToggleKey}
            onRegenerate={handleRegenerate}
            onDelete={setKeyToDelete}
            onCreateNew={() => setIsModalOpen(true)}
            copiedId={copied}
            isRegenerating={isRegenerating}
            moduleFilter={MODULE}
          />
        </div>
      </div>

      <TicketConfig moduleName="tickets" isActive={isTicketActive} />

      {/* Features */}
      <FeatureList features={features} />

      {/* Modales */}
      <ApiKeyModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateKey}
        formData={formData}
        setFormData={setFormData}
        moduleName={MODULE}
        permissions={PERMISSIONS}
        moduleLabels={MODULE_LABELS}
      />

      <ApiDeleteConfirmationModal
        keyToDelete={keyToDelete}
        onConfirm={handleDeleteKey}
        onCancel={() => setKeyToDelete(null)}
      />

      <NewKeyNotification newKey={newKeySecret} onClose={() => setNewKeySecret(null)} onCopy={copyToClipboard} />
    </div>
  );
};
